import { ActivityEntityName, DomainType, getPortalClientSectionRights, getPortalContractorSectionRights, SecurityRoleTiny, StageItemDetail, SurfaceType } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import type { EntityManifestRelations } from '@workbuddy/services/entities/entity.manifest.relations';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import type { EntityManifestBoardDeclaration, EntityManifestOperation, EntityManifestSurfaces } from '@workbuddy/services/entities/entity.manifest.surface';
import { ITEM_BROWSER_DIALOG_PRESENTATION } from '@workbuddy/ui/blocks/line.items';
import { embeddedItemBoardBase } from '@workbuddy/surfaces/embedded-item';
import { jobItemBoardProfile } from './board/profiles/job.item/job.item.board.profile';
import { jobTemplateItemBoardProfile } from './board/profiles/job.template.item.board.profile';
import { recurringItemBoardProfile } from './board/profiles/recurring.item.board.profile';
import { plannedItemBoardProfile } from './board/profiles/planned.item.board.profile';
import { loadJobItemDetailRecord } from './surface/detail/job.item.detail.record';


/**
 * JobItem entity manifest.
 *
 * Registered when this module loads at bootstrap. Consumers resolve via getManifest /
 * EntityManifestService only.
 *
 * JobItem roots the same job documents as Job but at its own metadata chain
 * (JobItem → workOrders → codes), so the items board inherits none of Job's column, filter or
 * grouping affordances. One row = one item.
 *
 * **Create** is `kind: 'custom'` (catalogue ItemBrowser dialog), not a Surface Engine form. The
 * board forwards host `params` on Add; the dialog reads job/stage and calls AddItems.
 *
 * The manifest owns the board and its inline editor. Hosts supply parent scope and live values;
 * the Job Item profile applies Items or SOR policy from those values.
 */

/** Custom dialog: multi-select catalogue → AddItems. Presentation is sized dialog, not pseudo-page. */
const createOperation: EntityManifestOperation<StageItemDetail> = {
  kind: 'custom',

  // Shared shell; JobItem's wording / catalogue narrowing / write arrive as params.itemBrowser.
  load: () => import('@workbuddy/entities/shared/item.browser.dialog').then(m => m.ItemBrowserDialogComponent),
  presentation: {
    mode: 'dialog',
    ...ITEM_BROWSER_DIALOG_PRESENTATION,
  },
};

/** SOR Details: one page for the office and the portal; each app's `ITEM_DETAIL_HOST` holds what differs (WBS-12033). */
const sorDetailOperation: EntityManifestOperation<StageItemDetail> = {
  kind: 'custom',
  load: () => import('./board/components/item.detail.page').then(m => m.ItemDetailPageComponent),
  presentation: { mode: 'pseudo-page' },
};

/** The slice of the portal store the client SOR page authorises against. */
type ClientPortalRoleState = { readonly application?: { readonly activeRole?: SecurityRoleTiny | null } };

const itemBoard: EntityManifestBoardDeclaration = {
  base: embeddedItemBoardBase,
  profiles: {
    [ActivityEntityName.JobItem]: jobItemBoardProfile,
    [ActivityEntityName.JobTemplateItem]: jobTemplateItemBoardProfile,
    [ActivityEntityName.RecurringJobTemplateItem]: recurringItemBoardProfile,
    [ActivityEntityName.PlannedItem]: plannedItemBoardProfile,
  },
};

const itemSurfaces: EntityManifestSurfaces<StageItemDetail> = {
  [SurfaceType.Detail]: () => import('./surface/detail/job.item.detail.surface').then(m => m.jobItemDetailSurface),
};

const itemRelations: EntityManifestRelations = {
  [ActivityEntityName.Task]: () => import('./relations/job.item.tasks.relation').then(m => m.jobItemTasksBoardBuilder),
};

export const jobItemManifest = registerManifest<StageItemDetail>({
  entity: ActivityEntityName.JobItem,
  slug: 'job-item',

  surfaces: itemSurfaces,

  relations: itemRelations,

  operations: {
    // edit: {
    //   kind: 'custom',
    //   load: () => import('./board/components/item.detail.page').then(m => m.ItemDetailPageComponent),
    //   presentation: { mode: 'pseudo-page' },
    // },
    expand: sorDetailOperation,
    preview: {
      surface: SurfaceType.Detail,
      presentation: { mode: 'pseudo-page' },
      loadRecord: loadJobItemDetailRecord,
    },
    create: createOperation,
  },

  board: itemBoard,

  // A domain inherits nothing (WBS-11932, WBS-11933), so the portal declares the same SOR page as the office.
  domains: {
    [DomainType.ContractorPortal]: {
      operations: {
        create: createOperation,
        // A page opened by its address needs a View the role holds; the line load answers for its own kind (WBS-12242).
        expand: {
          ...sorDetailOperation,
          authorize: ({ injector }) => {

            const sections = getPortalContractorSectionRights(injector.get(RightsService).role);

            return sections.items.view || sections.sors.view;
          },
        },
      },
      board: itemBoard,
    },
    // The client only reads, so the domain declares the board and the read-only SOR page; no create, no preview.
    [DomainType.ClientPortal]: {
      operations: {
        expand: {
          ...sorDetailOperation,
          // The portal store holds the client role as `application.activeRole`; it has no `application.role`.
          authorize: ({ injector }) => firstValueFrom(injector.get<Store<ClientPortalRoleState>>(Store).select(state => getPortalClientSectionRights(state.application?.activeRole).jobSors.view)),
        },
      },
      board: itemBoard,
    },
  },
});

/** The same board declaration for the other item entities; their entity name selects the profile. */
export const itemFamilyBoard = jobItemManifest.board;
